import React from "react";
import GenericGriddle, {
    plugins,
    GriddleSortKey,
    GriddleProps,
    GriddleComponents,
    components,
} from "griddle-react";

import { BasicLayout } from "./BasicLayout";
import { Layout } from "./Layout";
import { Filter } from "./Filter";
import { Pagination } from "./Pagination";
import { PageButtons } from "./PageButtons";
import { NoResults } from "./NoResults";
import { CellColspan, CellColspanProps } from "./Cells";

export interface TableProps<T> extends React.Props<Table<T>> {
    data: T[];
    defaultSorting?: GriddleSortKey | GriddleSortKey[];
    layout?: "basic";
    size?: "small";
    pageSize?: number;
    RowEnhancer?: (OriginalComponent: React.ComponentType<components.RowProps>)
        => React.ComponentType<components.RowProps>;
    CellEnhancer?: (OriginalComponent: React.ComponentType<CellColspanProps>)
        => React.ComponentType<CellColspanProps>;
}

export class Table<T> extends React.Component<TableProps<T>, any> {
    public render(): JSX.Element {
        const { data, size, layout, defaultSorting, RowEnhancer, CellEnhancer, children } = this.props;
        // Type the Griddle table first.
        type Griddle = new (p: GriddleProps<T>) => GenericGriddle<T>;
        const Griddle = GenericGriddle as Griddle;

        // Set the options
        const styleConfig = {
            classNames: {
                Table: "table" + (size === "small" ? " table-sm" : ""),
                Filter: "form-control",
                PageDropdown: "justify-content-center",
                NoResults: "table__empty",
            },
        };

        // Paging properties
        const pageSize = this.props.pageSize == null ? 20 : this.props.pageSize;
        const pageProperties = { pageSize };

        // Set the default sorting.
        let sortProperties: GriddleSortKey[] = [];
        if (defaultSorting != null) {
            if (Array.isArray(defaultSorting)) {
                sortProperties = defaultSorting;
            }
            else {
                sortProperties.push(defaultSorting);
            }
        }

        // Remove the settings from the layout.
        const componentsConfig: GriddleComponents = {
            Layout: layout == null ? Layout : BasicLayout,
            Filter,
            Pagination,
            PageDropdown: PageButtons,
            NoResults,
            Cell: CellColspan,
        };
        if (RowEnhancer != null) {
            componentsConfig.RowEnhancer = RowEnhancer;
        }
        if (CellEnhancer != null) {
            componentsConfig.CellEnhancer = CellEnhancer;
        }

        return (
            <Griddle
                storeKey="griddle"
                data={data}
                styleConfig={styleConfig}
                plugins={[plugins.LocalPlugin]}
                components={componentsConfig}
                pageProperties={pageProperties}
                sortProperties={sortProperties}
            >
                {children}
            </Griddle>
        );
    }
}
